<template>
  <button
    type="button"
    class="SeatCell"
    :class="[size && `SeatCell--${size}`, `SeatCell--${state}`, variantClass]"
    :disabled="isInert"
    :aria-pressed="state === 'selected'"
    :aria-label="ariaLabel"
  >
    <Icon
      v-if="state === 'unavailable'"
      icon="window-close"
      :size="size === 'sm' ? 'sm' : 'lg'"
      class="SeatCell-icon"
    />
    <span v-else class="SeatCell-label">{{ label }}</span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Icon from '../Icon/Icon.vue';
import type { SeatCellProps } from './SeatCell.model';

const props = withDefaults(defineProps<SeatCellProps>(), {
  size: 'md',
  label: '',
  state: 'available',
});

const isInert = computed(() => props.state === 'disabled' || props.state === 'unavailable');
const variantClass = computed(() => props.variant && `SeatCell--${props.variant}`);
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';

// Seat-class variants recolor the "available"/"unavailable" states only — selection and
// disabled-by-another-passenger are class-agnostic, matching the tokens Figma landed.
@mixin seat-class-variant(
  $enabled-bg-color,
  $enabled-border-color,
  $enabled-border-top-stroke,
  $enabled-text-color,
  $unavailable-bg-color,
  $unavailable-border-color,
  $unavailable-border-top-stroke,
  $unavailable-icon-color
) {
  &.SeatCell--available {
    background-color: $enabled-bg-color;
    border-color: $enabled-border-color;
    border-style: solid;
    border-block-start-width: $enabled-border-top-stroke;

    .SeatCell-label {
      color: $enabled-text-color;
    }
  }

  &.SeatCell--unavailable {
    background-color: $unavailable-bg-color;
    border-color: $unavailable-border-color;
    border-style: solid;
    border-block-start-width: $unavailable-border-top-stroke;

    .SeatCell-icon {
      color: $unavailable-icon-color;
    }
  }
}

@layer cvo-ui {
  .SeatCell {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border-width: vars.$atoms-seatcell-visualcue-baseinput-border-stroke;
    border-style: solid;
    border-radius: vars.$atoms-seatcell-visualcue-border-radius;
    padding: vars.$dimensions-d-1;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid vars.$color-neutral-gray-800;
      outline-offset: 2px;
    }

    &[disabled] {
      cursor: not-allowed;
    }

    &--md {
      width: 32px;
      height: 32px;

      .SeatCell-label {
        @include text.text('sm', vars.$typography-weight-strong);
      }
    }

    &--sm {
      width: 24px;
      height: 24px;

      .SeatCell-label {
        @include text.text('xs', vars.$typography-weight-strong);
      }
    }

    &--available {
      background-color: vars.$atoms-seatcell-visualcue-enabled-bg-color;
      border-color: vars.$atoms-seatcell-visualcue-enabled-border-color;
      border-style: dashed;

      .SeatCell-label {
        color: vars.$atoms-seatcell-visualcue-enabled-text-color;
      }
    }

    &--selected {
      background-color: vars.$atoms-seatcell-visualcue-selected-bg-color;
      border-color: vars.$atoms-seatcell-visualcue-selected-border-color;

      .SeatCell-label {
        color: vars.$atoms-seatcell-visualcue-selected-text-color;
      }
    }

    &--disabled {
      background-color: vars.$atoms-seatcell-visualcue-disabled-bg-color;
      border-color: vars.$atoms-seatcell-visualcue-disabled-border-color;

      .SeatCell-label {
        color: vars.$atoms-seatcell-visualcue-disabled-text-color;
      }
    }

    &--unavailable {
      background-color: vars.$atoms-seatcell-visualcue-unavailable-bg-color;
      border-color: vars.$atoms-seatcell-visualcue-unavailable-border-color;

      .SeatCell-icon {
        color: vars.$atoms-seatcell-visualcue-unavailable-icon-color;
      }
    }

    // TODO Ask Design to rename tokens "seata" "seatb" into a more meaningful name like "basic" and "premium"

    &--premium {
      @include seat-class-variant(
        $enabled-bg-color: vars.$atoms-seatcell-seata-enabled-bg-color,
        $enabled-border-color: vars.$atoms-seatcell-seata-enabled-border-color,
        $enabled-border-top-stroke: vars.$atoms-seatcell-seata-enabled-border-top-stroke,
        $enabled-text-color: vars.$atoms-seatcell-seata-enabled-text-color,
        $unavailable-bg-color: vars.$atoms-seatcell-seata-unavailable-bg-color,
        $unavailable-border-color: vars.$atoms-seatcell-seata-unavailable-border-color,
        $unavailable-border-top-stroke: vars.$atoms-seatcell-seata-unavailable-border-top-stroke,
        $unavailable-icon-color: vars.$atoms-seatcell-seata-unavailable-icon-color
      );
    }

    &--basic {
      @include seat-class-variant(
        $enabled-bg-color: vars.$atoms-seatcell-seatb-enabled-bg-color,
        $enabled-border-color: vars.$atoms-seatcell-seatb-enabled-border-color,
        $enabled-border-top-stroke: vars.$atoms-seatcell-seatb-enabled-border-top-stroke,
        $enabled-text-color: vars.$atoms-seatcell-seatb-enabled-text-color,
        $unavailable-bg-color: vars.$atoms-seatcell-seatb-unavailable-bg-color,
        $unavailable-border-color: vars.$atoms-seatcell-seatb-unavailable-border-color,
        $unavailable-border-top-stroke: vars.$atoms-seatcell-seatb-unavailable-border-top-stroke,
        $unavailable-icon-color: vars.$atoms-seatcell-seatb-unavailable-icon-color
      );
    }

    &-label {
      line-height: 1;
      text-align: center;
      width: 100%;
    }
  }
}
</style>
